import { BOT_ACTION_CONDITION_TYPE, type BotActionConditionType } from './constants'
import type { StrengthTier } from '@ppwr/poker-engine/utils'

type CopyTemplateArgs = Record<string, string>

type CopyTemplate = string | ((args: CopyTemplateArgs) => string)

/**
 * Placeholder semantics:
 *
 * - `{hand}` / `{reasonHint}` / `{recommendedAction}` — replaced verbatim
 *   with their concrete values.
 *
 * - `{facing}` — what the hero is being asked to call, in chips ("a raise to
 *   120 chips" preflop, "a bet of 120 chips (about half the pot)" postflop).
 *   Built in advisor.ts (describeFacingBet); always a noun phrase starting
 *   with an article, so templates should place it where a noun phrase fits
 *   ("you are facing {facing}", "{facing} is worth paying"), never after
 *   "the" or "this".
 *
 * - `{street}` — a plain beginner-friendly street phrase (e.g.
 *   "with the first three shared cards showing").
 *
 * - `{describedAction}` — an action verb phrase ("fold", "raise", "go all-in").
 *
 * - `{because}` — always prepended with a leading space and a rotated
 *   connective (`" because ..."` / `" since ..."`) by the caller, so templates
 *   should append it directly after the preceding word without adding a space
 *   themselves:
 *   `"Choose to {recommendedAction} instead{because}."`
 *
 * The frontend shows a single supporting line (`differenceReason ??
 * primaryReason`), so the difference line must carry the reason itself —
 * it is the only explanation the player sees when the grades differ.
 */

function hashString(input: string): number {
  let hash = 0
  for (let index = 0; index < input.length; index += 1) {
    hash = (hash * 31 + input.charCodeAt(index)) >>> 0
  }

  // Avalanche (fmix32, the MurmurHash3 finalizer). The raw polynomial hash
  // correlates seeds that differ only in their trailing hand number: for
  // two-digit hand numbers with a units digit of 3 or more, N -> N+7 shifts
  // the hash by exactly 31^2 * 28, a multiple of 28, so every 14-entry pool
  // repeated the same template every 7 hands (players folding junk preflop
  // read the identical line three times in a row). The avalanche step mixes
  // the high bits into the low bits, so small seed changes spread across the
  // whole range while the hash stays fully deterministic.
  hash = (hash ^ (hash >>> 16)) >>> 0
  hash = Math.imul(hash, 0x85ebca6b) >>> 0
  hash = (hash ^ (hash >>> 13)) >>> 0
  hash = Math.imul(hash, 0xc2b2ae35) >>> 0
  hash = (hash ^ (hash >>> 16)) >>> 0
  return hash
}

function renderTemplate(template: CopyTemplate, args: CopyTemplateArgs): string {
  if (typeof template === 'function') {
    return template(args)
  }

  // Keep replacement values in their grammatical form. Reason hints are
  // deliberately lower-case clauses because they are often inserted after
  // "because"; sentence-start templates capitalize them explicitly.
  return template.replace(/\{(\w+)\}/g, (_match, key: string) => args[key] ?? '')
}

function capitalizeFirst(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

/**
 * Soft character budget for the bubble's single supporting line. The speech
 * bubble is content-sized and long lines push it out of shape, so advisor.ts
 * (capSupportingLine) drops the pot-share parenthesis when a composed line
 * exceeds this, and the advisor tests assert composed lines stay within it.
 */
export const ADVISOR_SUPPORTING_LINE_BUDGET = 125
export function pickAdvisorCopy(
  seed: string,
  templates: readonly CopyTemplate[],
  args: CopyTemplateArgs,
  offset = 0
): string {
  const template = templates[(hashString(seed) + offset) % templates.length]
  return renderTemplate(template, args)
}

// The headline already names the action. The supporting line should carry one reason,
// not restate every relevant poker concept in the spot. Real wording variety comes
// from the rotated reason hints — the supporting line is the bare hint clause,
// capitalized as a sentence. (An earlier version rotated a "The key is that …"
// shell in here; with a 2-item pool it fired ~50% of the time and read as a
// verbal tic, especially in pre-play advice where the supporting line is
// always the primary reason.)

// A copy pool split by street validity — the advisor's river-safety mechanism.
// `allStreets` entries are true on any street (river included). `preRiverOnly`
// entries reference future cards or a "better spot" to wait for — framing
// that is FALSE on the river, where the board is complete and a fold or
// check simply ends the hand. Each string exists exactly once in exactly one
// array: no separate filter list to keep in sync, no string matching to
// drift. `streetAwareCopyPool` merges the halves on pre-river streets and
// returns only the always-true entries on the river. New hints that promise
// a later card or a safer spot belong in `preRiverOnly`.
export type StreetAwareCopyPool = {
  allStreets: readonly string[]
  preRiverOnly: readonly string[]
}

export function streetAwareCopyPool(pool: StreetAwareCopyPool, isRiver: boolean): readonly string[] {
  return isRiver ? pool.allStreets : [...pool.allStreets, ...pool.preRiverOnly]
}

export const ADVISOR_PRIMARY_REASON_COPY = {
  foldWithHint: [(args: CopyTemplateArgs) => `${capitalizeFirst(args.reasonHint)}.`],
  foldDefault: {
    allStreets: [
      'This hand is not worth {facing}.',
      'There is not enough value to continue.',
      '{facing} asks too much of this hand.',
      'Let this one go.',
      '{facing} is not worth what this hand can win.'
    ],
    preRiverOnly: ['Save your chips for a stronger spot.', 'A fold keeps more chips for the next opportunity.']
  },
  continueWithHint: [(args: CopyTemplateArgs) => `${capitalizeFirst(args.reasonHint)}.`],
  continueDefault: [
    'This hand has enough value to continue.',
    'This hand is worth the cost of continuing.',
    'There is enough strength to keep going.',
    'This hand is still worth playing.',
    'This hand offers enough to keep going.',
    'Staying in is worth it here.',
    'This hand has earned its seat.'
  ],
  pressureWithHint: [(args: CopyTemplateArgs) => `${capitalizeFirst(args.reasonHint)}.`],
  pressureDefault: [
    'This is a good spot to add pressure.',
    'Your hand is strong enough to add chips.',
    'A bet can collect from weaker hands.',
    'Taking the lead fits this spot.',
    'The hand supports a bigger pot.',
    'This is a moment to build the pot.',
    'Your hand justifies putting chips in.'
  ]
} as const

// Difference copy names the adjustment and one reason. `{because}` begins with
// a rotated " because" / " since" when a concrete reason is available — this line is the only
// explanation the player sees when the grades differ, so it must carry the
// "why", not just the adjustment.

export const ADVISOR_DIFFERENCE_REASON_COPY = {
  smaller: [
    'Use a smaller size{because}.',
    'Size down{because}.',
    'The idea works, but bet smaller{because}.',
    'Keep the bet smaller{because}.',
    'Trim the size{because}.',
    'Use fewer chips here{because}.',
    'A lighter bet works better{because}.',
    'Keep the pressure, but use less{because}.'
  ],
  larger: [
    'Use a larger size{because}.',
    'Size up{because}.',
    'The idea works, but bet larger{because}.',
    'Choose a bigger bet{because}.',
    'Put in a little more{because}.',
    'Build the pot with a larger size{because}.',
    'Use more chips for this bet{because}.',
    'Keep the action, but increase the size{because}.'
  ],
  avoidJam: [
    'No need to go all-in{because}.',
    'Keep some chips back{because}.',
    'A smaller commitment fits better{because}.',
    'Use a more measured size{because}.',
    'Apply pressure without going all-in{because}.',
    'Leave room to play later streets{because}.',
    'Commit fewer chips here{because}.',
    'A controlled size does the job{because}.'
  ],
  oversizedValue: [
    'Use a smaller size{oversizedBecause}.',
    'Size down{oversizedBecause}.',
    'Keep the size measured{oversizedBecause}.',
    'The smaller size works better{oversizedBecause}.',
    'Scale the bet back{oversizedBecause}.',
    'A smaller size earns more{oversizedBecause}.'
  ],
  oversizedDraw: [
    'Use a smaller size{oversizedBecause}.',
    'Size down{oversizedBecause}.',
    'Keep the commitment measured{oversizedBecause}.',
    'Avoid the overbet{oversizedBecause}.',
    'Keep the draw cheap{oversizedBecause}.',
    'A smaller bet fits the draw better{oversizedBecause}.'
  ],
  oversizedContinue: [
    'Use a smaller size; {recommendedAction} is enough here{oversizedBecause}.',
    'Avoid the overbet; {recommendedAction} is the steadier choice{oversizedBecause}.',
    'Keep the pot manageable; {recommendedAction} fits this spot{oversizedBecause}.',
    'Choose the smaller commitment; {recommendedAction} is all this hand needs{oversizedBecause}.',
    'Ease off the size{oversizedBecause}.',
    'Keep it modest; {recommendedAction} does the job{oversizedBecause}.'
  ],
  overbetInsteadOfCall: [
    'Call instead of raising{oversizedBecause}.',
    'The overbet is too much; call{oversizedBecause}.',
    'Keep the pot smaller and call{oversizedBecause}.',
    'Call and avoid putting in more than necessary{oversizedBecause}.',
    'Calling keeps the risk in check{oversizedBecause}.',
    'Call for less; the pot does not need more{oversizedBecause}.'
  ],
  // Avoid mentioning chips/stack here: {because} is frequently filled by
  // buildFoldReason (advisor.ts), which already appends its own chip-saving
  // closer ("so save your chips", "so protect your stack", etc.). A second
  // chip mention in this template reads as redundant.
  foldInstead: [
    'Fold here{because}.',
    'Fold this hand{because}.',
    'Stay patient and fold{because}.',
    'Choose to fold{because}.',
    'Pass on this hand{because}.',
    'Let it go{because}.',
    'Fold with discipline{because}.',
    'Fold and wait{because}.'
  ],
  continueInsteadOfFold: [
    'Choose to {recommendedAction} instead{because}.',
    'Keep playing; {recommendedAction}{because}.',
    'Stay in with a {recommendedAction}{because}.',
    'There is enough here to {recommendedAction}{because}.',
    'Give this hand a chance; {recommendedAction}{because}.',
    'Continue with a {recommendedAction}{because}.',
    'Keep the hand alive with a {recommendedAction}{because}.',
    'Stay involved and {recommendedAction}{because}.'
  ],
  lessAggressive: [
    'Choose to {recommendedAction} instead{because}.',
    'Keep it simple and {recommendedAction}{because}.',
    'Control the pot with a {recommendedAction}{because}.',
    'Your idea is reasonable; {recommendedAction} is the steadier choice{because}.',
    'Slow down and {recommendedAction}{because}.',
    'Use the measured line and {recommendedAction}{because}.',
    'Avoid forcing the action; {recommendedAction}{because}.',
    'Keep the pot manageable and {recommendedAction}{because}.'
  ],
  pressureInsteadOfFold: [
    'This is a good spot to apply pressure{because}.',
    'Stay in and add pressure{because}.',
    'This hand is strong enough to apply pressure{because}.',
    'Keep the initiative{because}.',
    'Continue and make them decide{because}.',
    'Give this strong hand a chance to lead{because}.',
    'Stay involved with an aggressive action{because}.',
    'Use this hand to push the action{because}.'
  ],
  pressureInsteadOfPassive: [
    'Choose to {recommendedAction} instead{because}.',
    'Take the lead and {recommendedAction}{because}.',
    'Use the strength of this hand and {recommendedAction}{because}.',
    'Add pressure and {recommendedAction}{because}.',
    'Step up the aggression and {recommendedAction}{because}.',
    'Grow the pot and {recommendedAction}{because}.',
    'Make the stronger move and {recommendedAction}{because}.',
    'Put the other player to a decision; {recommendedAction}{because}.'
  ],
  generic: [
    'Choose to {describedAction}{because}.',
    'The better action is to {describedAction}{because}.',
    'This spot calls for you to {describedAction}{because}.',
    'Keep it simple and {describedAction}{because}.',
    'Look to {describedAction}{because}.',
    'The sound play is to {describedAction}{because}.',
    'Take this line: {describedAction}{because}.',
    'A better fit is to {describedAction}{because}.'
  ]
} as const

// The reason fragments spliced into the `{oversizedBecause}` placeholder of
// the oversized* templates above (`getDifferenceReasonContext`, advisor.ts).
// One pool per fact-branch, selected BEFORE rotation: 'draw' when the
// player's hand is a draw (the reason must state the draw needs to improve),
// 'value' when the better line is another bet/raise (too-large bets fold out
// the weaker callers the bet wants), 'generic' otherwise. Keeping each branch
// its own pool means phrasing rotation can never cross fact boundaries — a
// draw-hand reason can never leak into a made-hand spot. One entry each for
// now: the line-level rotation (connective + template) already prevents
// verbatim repeats, so variety here is optional, not needed.
export const ADVISOR_OVERSIZED_REASON = {
  draw: [
    'this draw still needs to improve, so the extra risk is not necessary',
    'the improvement is not guaranteed, so keep the risk smaller',
    'one more card has to come in before this hand can carry a big bet'
  ],
  value: [
    'betting too large can push out weaker hands you want to call',
    'a smaller size keeps the weaker hands in instead of folding them out',
    'the bigger the bet, the more only stronger hands stay'
  ],
  generic: [
    'the overbet risks more than this spot requires',
    'the extra chips raise the risk without adding much',
    'a smaller size does the same job with less exposure'
  ]
} as const

// Reason hints for common made-hand spots (pair or better, no draw), bucketed by
// hand strength. These are the most frequent postflop decisions but rarely match a
// condition-specific hint, so without them they fall through to the thin generic
// action-type pool. Each hint names the "why" (ahead of bluffs, worse hands pay,
// price is right) rather than just asserting the hand is strong. Hints are
// declarative clauses that read correctly both inside "The key is that {reasonHint}."
// and as a standalone capitalized sentence.
export const MADE_HAND_CALL_HINTS: Record<'strong' | 'topPair' | 'pair', StreetAwareCopyPool> = {
  strong: {
    allStreets: [
      // Monster hands are not price decisions — calling is about keeping worse
      // hands in and letting bluffs keep betting, so avoid pot-odds framing here
      // (that belongs to the marginal 'pair' bucket below).
      'calling with {hand} keeps the weaker hands in the pot',
      '{hand} is far ahead, so calling lets the worse hands keep betting',
      'a bet into {hand} usually means a worse hand or a bluff, so let them keep putting chips in',
      'worse hands can keep paying {hand} off, and calling keeps them around',
      '{hand} is strong enough to call and let the pot grow',
      'with {hand} you are far ahead, so there is no rush to raise',
      'calling keeps the pressure on their bluffs while {hand} sits ahead',
      'with {hand}, patient calling collects more than raising would',
      '{hand} is strong enough that slower play earns just as much',
      'let them keep betting into {hand}; calling invites exactly that'
    ],
    preRiverOnly: []
  },
  topPair: {
    allStreets: [
      '{facing} is worth paying since top pair beats the bluffs and the worse pairs',
      'top pair is ahead often enough that {facing} pays for itself',
      'a top pair is strong enough for {facing}; you only trail a few better hands',
      '{facing} is fair for top pair, which is ahead of the hands that will keep betting',
      'this bet usually means a worse hand or a bluff, so top pair can pay {facing}',
      'top pair is ahead often enough to pay {facing}',
      'top pair is worth {facing} against most of what they could have',
      'paying {facing} with top pair keeps you in control of the hand',
      'for {facing}, top pair is the hand the others are trying to catch',
      '{facing} is small for how often top pair stands best here'
    ],
    preRiverOnly: []
  },
  pair: {
    allStreets: [
      '{facing} is worth it for {hand}, though a few hands are still ahead',
      'your pair is strong enough for {facing}, but be cautious if the betting grows',
      'a pair is worth {facing} to continue, even if you are not always ahead',
      '{facing} is low enough for {hand}; fold if they push harder',
      '{facing} is small enough to keep {hand} alive without risking much',
      '{facing} is fair for the chance {hand} holds',
      '{facing} is small next to what {hand} could win'
    ],
    preRiverOnly: [
      '{facing} is cheap enough that {hand} can improve or win as is',
      'for {facing}, {hand} is worth one more card',
      '{facing} is small, and {hand} still has outs if it is behind'
    ]
  }
} as const

export const MADE_HAND_CHECK_HINTS: Record<'strong' | 'topPair' | 'pair', StreetAwareCopyPool> = {
  strong: {
    allStreets: [
      'checking keeps the pot small while {hand} is still ahead',
      'checking lets {hand} keep the lead without growing the pot',
      'checking keeps {hand} in control while the pot stays small',
      'there is no rush to bet; {hand} is comfortably ahead',
      'checking lets {hand} collect more without forcing anyone out',
      'with {hand} this strong, a quiet pot pays just as well',
      'checking keeps {hand} ahead and the decisions easy',
      'there is no need to rush with {hand}; the hand plays itself'
    ],
    preRiverOnly: [
      'with {hand} you can control the pot and see another card',
      'a free card is safe with {hand}; you do not need to protect yet'
    ]
  },
  topPair: {
    allStreets: [
      'checking keeps the pot manageable with top pair on a quiet board',
      'checking controls the pot with top pair instead of building a bigger one',
      'with top pair, a check keeps the pot small and the decision easy',
      'checking with top pair lets a worse hand do the betting instead',
      'top pair is ahead often enough to keep this pot calm',
      'checking keeps top pair out of trouble on this board',
      'with top pair, keeping it cheap keeps them guessing',
      'a check with top pair keeps the risk small while staying ahead'
    ],
    preRiverOnly: [
      'a free card is fine with top pair when the board is not too threatening',
      'with top pair, checking keeps the pot small if a scarier card comes'
    ]
  },
  pair: {
    allStreets: [
      'checking avoids putting more in with {hand}',
      'with {hand}, keeping the pot small is the safer line',
      'checking keeps {hand} in without paying to find out where you stand',
      'a check with {hand} costs nothing and keeps the options open',
      'checking with {hand} keeps the decision cheap',
      'with {hand}, there is no need to pay for information',
      'a check keeps {hand} patient and the pot in reach',
      'checking lets {hand} see where it stands for free'
    ],
    preRiverOnly: [
      'a check with {hand} waits for a safer spot to commit',
      'checking keeps the pot small for {hand} to continue later'
    ]
  }
} as const

// Fold-closer connectives used by `buildFoldReason` (advisor.ts). Split the
// same way: the pre-river closers lean on future spots/opportunities, the
// allStreets closers stay true when the board is complete.
export const FOLD_REASON_CLOSERS: StreetAwareCopyPool = {
  allStreets: [
    '{reason}, so save your chips',
    '{reason}, so protect your stack',
    '{reason} — let this one go',
    '{reason}, so hold on to your chips'
  ],
  preRiverOnly: [
    '{reason}; wait for a better spot',
    '{reason}, so keep chips for a stronger opportunity',
    '{reason}; better spots are coming',
    '{reason} — keep your stack for a stronger spot',
    '{reason}, so release this hand',
    '{reason}; staying in here is too expensive',
    '{reason}, so step out of the way',
    '{reason} — this is not the spot to force it'
  ]
}

export const MADE_HAND_VALUE_HINTS: Record<'strong' | 'topPair' | 'pair', readonly string[]> = {
  strong: [
    'betting {hand} collects from worse hands',
    'with {hand} you are ahead, so build the pot',
    'a bet with {hand} makes the draws pay to catch up',
    'betting {hand} grows the pot while you are ahead',
    'betting {hand} builds the pot while worse hands keep calling',
    'a bet with {hand} charges every hand that is chasing',
    '{hand} is strong enough that betting collects from nearly everything behind',
    'the pot should grow while {hand} is the one to catch',
    'with {hand}, charging them now beats hoping they bet later',
    'a bet with {hand} turns a strong hand into a bigger one'
  ],
  topPair: [
    'betting top pair earns calls from worse pairs and draws',
    'with top pair you are ahead often enough to build the pot',
    'a bet with top pair makes the draws and worse pairs pay',
    'betting top pair earns calls from weaker hands that are behind',
    'a bet with top pair makes worse pairs and draws pay to continue',
    'top pair is ahead often enough to bet here',
    'top pair earns its keep when the worse pairs pay to see another card',
    'a bet with top pair charges the hands that are chasing',
    'with top pair ahead often enough, the bet collects from the followers',
    'betting top pair now beats finding out the hard way later'
  ],
  pair: [
    'betting {hand} makes the draws pay to catch up',
    'with {hand} a measured bet collects without pushing out worse hands',
    'a bet with {hand} can collect from worse hands at this size',
    '{hand} is ahead often enough that a bet collects from worse hands',
    'a modest bet with {hand} keeps the weaker hands paying',
    'with {hand}, a small bet earns more than it risks'
  ]
} as const

// The connectives that join a difference template's action clause to its
// reason (the caller prepends " because ..." / " since ..." / " — ...",
// leading space included, to fill `{because}` / `{oversizedBecause}`). Both
// word connectives need a declarative clause after them, which reason hints
// are. "as" is deliberately absent — after a bet-size noun phrase it misreads
// as the preposition ("... larger size as a raise with ..."). The em-dash
// variant sidesteps connective ambiguity entirely: a dash cannot attach to
// the preceding noun, so it is safe after sizing lead-ins where "as" fails.
export const DIFFERENCE_CONNECTIVES = ['because', 'since', '—'] as const

// Contrastive passive hints — WHY the aggressive action is wrong for this
// hand (buildContrastivePassiveHint, advisor.ts), used on "too aggressive,
// the passive line is better" spots. Unlike the pro-recommendation hints
// (which justify calling/checking), these address the aggressive action
// directly, so the difference line does not read as recycled aligned-with-
// call feedback. Phrasing is deliberately bet/raise-agnostic ("more
// pressure", "a bigger pot") since the player's action can be either.
// Draw hands and made hands fail for different reasons, so they get
// separate pools.
export const CONTRASTIVE_PASSIVE_HINTS = {
  // Board-only made hands ("only the straight on the board") fail for a
  // different reason than private made hands: the hand is shared, so betting
  // has little to gain. Every template names {hand} so the copy always makes
  // clear the hand came from the shared cards, not the player's pockets.
  boardOnlyMadeHand: [
    '{hand} is already shared by everyone still in',
    'a bet with {hand} mostly gets called by hands just as strong',
    '{hand} gives no edge over the other players',
    'with {hand}, there is little to gain from a bigger pot'
  ],
  draw: [
    'the hands your draw wants to keep in the pot can fold to more pressure',
    'the draw wants a cheap card more than a bigger pot',
    'a bigger pot adds risk while the draw is still incomplete',
    'the draw is still one card short of a strong hand'
  ],
  made: [
    'the weaker hands you want to keep paying can fold to more pressure',
    'a bigger pot needs a stronger hand than {hand}',
    'building the pot favors the stronger hands here',
    'the pot should stay small while {hand} can still be beaten'
  ]
} as const

// Generic postflop action-type reason hints — the fallback when no
// condition-specific or made-hand hint matched (buildPostflopReasonHint /
// buildPostflopFoldReasonHint, advisor.ts). `fold` entries get a fold closer
// appended by buildFoldReason, so they read standalone too.
export const POSTFLOP_ACTION_HINTS = {
  call: [
    '{hand} is strong enough for {facing}, so keep playing',
    '{facing} is reasonable for {hand}',
    '{hand} has enough value to continue',
    'calling {facing} keeps {hand} in the hand'
  ],
  check: [
    '{hand} is not strong enough to bet, so keep the pot small',
    'checking controls the pot with {hand}',
    '{hand} has more value checking than building a bigger pot',
    'a free card is better than forcing the action with {hand}'
  ],
  value: [
    '{hand} is strong enough to collect from weaker hands',
    'weaker hands can pay off {hand}',
    '{hand} is ahead often enough to build the pot',
    'betting {hand} can earn calls from worse hands'
  ],
  fold: [
    '{hand} does not have enough value for {facing}',
    '{hand} is not strong enough to pay {facing}',
    '{facing} is too much for {hand}',
    '{hand} will not win often enough to pay {facing}',
    '{facing} buys little chance for {hand}',
    'paying here mostly feeds a stronger hand than {hand}',
    '{facing} is steep for what {hand} can win',
    '{hand} does not justify {facing}'
  ]
} as const

// Preflop reason hints (buildPreflopReasonHint, advisor.ts). Explainer
// style: each hint describes the situation in plain words (what you hold,
// what the other player did, what you are being asked to call in chips) and
// lets that explain the action, rather than asserting poker idioms. Fold copy is
// split by what the hero is actually facing: naming a raise when the hero
// only faces the blinds (or a limp) reads wrong, and the two spots justify
// the fold differently. Larger pools keep the very frequent preflop-fold
// feedback from repeating the same phrasing.
export const PREFLOP_REASON_HINTS = {
  foldFacingRaise: [
    'you are facing {facing}, and {hand} is too weak to keep going',
    'a raise usually means a strong hand, and {hand} cannot compete with that',
    'the other player is showing strength, and {hand} is not strong enough to answer',
    'calling {facing} with {hand} costs too much for what it can win',
    '{facing} is more than {hand} is worth',
    'a raise this size needs a strong hand, and {hand} is not one',
    'the raise costs more to stay in, and {hand} does not win often enough to pay it',
    'paying the raise with {hand} risks chips for a small chance of winning',
    'someone has already raised, and {hand} is not strong enough to call it',
    'the raise points to a strong hand, and {hand} is better off out of the way',
    '{facing} is a lot to pay for {hand}',
    'matching {facing} with {hand} is paying too much for a weak start',
    'a raise this early is usually a confident hand, and {hand} cannot match it'
  ],
  foldFacingBlinds: [
    'you are only facing {facing}, but {hand} is still too weak to play',
    'no one has raised, and {hand} is still a losing hand even for {facing}',
    '{facing} is not the problem; {hand} is simply too weak',
    '{hand} misses the flop too often to be worth {facing}',
    '{facing} is small, but {hand} has little chance of winning the pot',
    'the only cost of folding is the blind, and {hand} is unlikely to win anyway',
    'you are only facing the blinds, but {hand} is still too weak to bother',
    'most hands fold in spots like this, and {hand} is one of them',
    '{facing} buys only a small chance with {hand}',
    'staying in costs little here, but {hand} would still be paying to lose',
    'hands like {hand} are better left alone, even for {facing}'
  ],
  call: [
    'for {facing}, {hand} is worth staying in',
    '{facing} is worth paying with {hand}',
    '{hand} is strong enough to pay {facing}',
    '{hand} is worth {facing} to keep playing',
    'calling {facing} keeps {hand} in the hand',
    'for {facing}, {hand} gets to see the first three shared cards',
    '{hand} plays well enough to continue for {facing}',
    '{facing} buys {hand} a real chance at the pot',
    '{hand} has enough promise to pay {facing}',
    'for {facing}, {hand} gets a fair shot at the flop',
    '{facing} is worth it for a hand like {hand}',
    'calling {facing} keeps {hand} alive without overcommitting',
    'staying in for {facing} lets {hand} win its share of pots',
    '{hand} is worth staying in for {facing}'
  ],
  check: [
    'checking is free, so there is no reason to pay with {hand}',
    'checking lets you see the flop without putting in more chips',
    '{hand} is not strong enough to raise, but checking costs nothing',
    'you can see the first three shared cards without paying more',
    'with {hand}, the free look is the safe play',
    'there is nothing to gain from paying when checking is free',
    'checking keeps you in the hand at no cost',
    'the free flop gives {hand} a chance to improve without any risk',
    'with {hand}, waiting for the flop is cheaper than pushing now'
  ],
  raise: [
    'a strong hand like {hand} wins more when the pot grows early',
    'raising builds the pot while {hand} is likely ahead',
    'with {hand}, raising puts the weaker hands under pressure',
    '{hand} is strong from the start, so it makes sense to take the lead',
    'raising now can win the pot right away, or build it while {hand} is ahead',
    '{hand} is strong enough to put the weaker hands to a decision',
    'raising with {hand} makes the hand more expensive for the weaker hands',
    '{hand} is strong enough that a bigger pot works in your favor',
    'raising with {hand} takes control of the hand early',
    'with a hand this strong, growing the pot early pays off',
    '{hand} is ahead of most hands right now, so build the pot while it is ahead'
  ]
} as const

// Tier-gated preflop claims, appended to the matching PREFLOP_REASON_HINTS pool for
// the hero's ACTUAL starting-hand tier (from the 169-class preflop equity table —
// strengthTier in poker-engine/utils/preflopEquity.ts). Absolute hand-strength
// claims ('one of the weakest starting hands', 'one of the best starting hands')
// are only true for the tier that earns them: king-ten offsuit folds from an
// early seat, but it is a middling hand, not one of the weakest, so the base
// pools carry only claims true for every hand in the bucket and the absolutes
// live here, gated on measured equity. Claims ride the same {hand}/{facing}
// fillers and the same rotation as the base pool. Tiers that never reach a
// pool are simply absent (Partial): folds never carry premium/strong claims,
// raises never carry marginal/muck claims.
export const PREFLOP_TIER_CLAIMS: Record<
  'foldFacingRaise' | 'foldFacingBlinds' | 'raise',
  Partial<Record<StrengthTier, readonly string[]>>
> = {
  foldFacingRaise: {
    muck: ['{hand} is one of the weaker hands, and it loses too often after paying a raise'],
    marginal: ['{hand} is a middling starting hand, and a raise needs a better answer']
  },
  foldFacingBlinds: {
    muck: [
      '{hand} is one of the weakest starting hands, and {facing} does not change that',
      'weak hands like {hand} lose more often than they win, even for {facing}',
      '{facing} is not worth paying for a hand as weak as {hand}'
    ],
    marginal: ['{hand} is a middling starting hand, and {facing} does not make it worth playing']
  },
  raise: {
    premium: ['{hand} is one of the best starting hands, and it is worth a bigger pot'],
    strong: ['{hand} is a strong starting hand, and it is worth a bigger pot']
  }
} as const

// Condition-specific postflop reason hints (buildPostflopConditionHint,
// advisor.ts) — tried before the made-hand and generic action-type hints.
// Each key maps to the bot-decision condition that unlocks it.
export const POSTFLOP_CONDITION_HINTS = {
  riverBluff: [
    'this is a credible bluffing spot that can push out weak hands',
    'a bet can represent strength and push out better hands',
    'the river gives this bluff a believable story',
    'enough weak hands can fold to make this bluff worthwhile',
    'the completed board helps a bet tell a convincing story',
    'the right size can push out hands that are only medium strong'
  ],
  turnGiveUp: [
    'a second bluff is unlikely to work often enough to justify the risk',
    'another bluff risks more chips without enough fold pressure',
    'the turn is a good place to stop applying pressure',
    'checking avoids forcing a bluff that will not work often enough',
    'the follow-up bet is unlikely to earn enough folds',
    'checking keeps the risk down when the bluff has run its course'
  ],
  potOddsFail: [
    '{hand} will not win often enough to justify {facing}',
    '{facing} is too much for the chance {hand} improves',
    'the potential reward does not cover the cost of continuing',
    '{hand} will not win often enough to pay {facing}',
    'the price outstrips the chance {hand} improves',
    '{facing} costs more than the chance it buys'
  ],
  riverValueBet: [
    '{hand} can still earn a call from weaker hands',
    '{hand} is still worth a bet on the river',
    'worse hands can pay off a river bet from {hand}',
    '{hand} is strong enough for one more bet on the final card',
    'a final-card bet can collect from the hands that miss',
    'weaker hands can still pay off one more bet'
  ],
  checkedTo: [
    'everyone checked, so a bet can win now without a strong hand',
    'the checks show weakness, giving a bet a chance to take the pot',
    'no one has shown strength, so this is a good pressure spot',
    'betting into the checks can push out other weak hands',
    'the checks leave the pot open for a bet',
    'no one has stepped up, so a bet can claim the pot'
  ],
  draw: [
    '{hand} can improve often enough to keep playing',
    '{hand} has enough ways to improve to continue',
    '{hand} will hit often enough to be worth {facing}',
    '{hand} can turn into a winning hand often enough from here',
    'one more card gives {hand} real winning chances',
    'the price is right for a hand that can still improve'
  ],
  preflopInitiative: [
    'your preflop raise makes continued pressure believable',
    'taking the lead preflop gives this bet credibility',
    'the earlier raise supports another measured bet',
    'continuing the preflop story can push out weaker hands',
    'the story you started preflop supports another bet',
    'your early lead makes this bet believable'
  ]
} as const

// Fact-bearing companions to POSTFLOP_CONDITION_HINTS.preflopInitiative, used
// only when the hero has a real made hand or draw (advisor.ts gates on the
// hand label before appending them). The base pool never names the hero's
// hand, so after a preflop raise every postflop bet recommendation read as
// "bet because you raised preflop", with no other context; these entries
// carry {hand} so the line names what the hero actually holds. They are
// appended to the base pool (same rotation seed and fillers), mirroring the
// PREFLOP_TIER_CLAIMS pattern: claims that need a real hand ride a gated
// pool instead of weakening the always-true base entries.
export const POSTFLOP_INITIATIVE_FACT_HINTS = [
  'the lead you took preflop plus {hand} gives this bet real credibility',
  '{hand} and the preflop lead together make this bet believable',
  'your preflop raise set the story, and {hand} backs it up now',
  'the pressure you showed preflop and {hand} both support another bet',
  'the preflop lead gives this bet credibility, and {hand} strengthens the story',
  'your early lead and {hand} together carry this bet'
] as const

// Generic condition phrases as a last-resort fallback when no concrete fact
// clause could be built (describeConditions, advisor.ts). Only conditions
// that the advisor can actually encounter are handled; internal routing
// conditions (aggressorRaise, defaultPlayerAction, fallbackStreetState,
// insufficientChips, noAvailableActions, noMatch, nothingToDo,
// suitedConnectors, tableMinimumRaise) are intentionally omitted — they
// signal the bot's internal state machine, not a concept a player should
// ever read about. Entries are either a plain string or a river split:
// "keep chips for a better spot" framing reads wrong on the river — the
// board is complete, so a fold just ends the hand instead of waiting.
export type GenericConditionPhrase = string | { preRiver: string; river: string }

export const GENERIC_CONDITION_PHRASES: Partial<Record<BotActionConditionType, GenericConditionPhrase>> = {
  [BOT_ACTION_CONDITION_TYPE.excellentHand]:
    'your hand is strong enough that you can confidently add chips',
  [BOT_ACTION_CONDITION_TYPE.strongHand]:
    'your hand is strong enough that you can confidently add chips',
  [BOT_ACTION_CONDITION_TYPE.strongMadeHand]:
    'your hand is strong enough that you can confidently add chips',
  [BOT_ACTION_CONDITION_TYPE.draws]:
    'your hand has a good enough chance of improving to be worth keeping going',
  [BOT_ACTION_CONDITION_TYPE.semiBluffRaise]:
    'your hand could win right now, and it still has a good chance to improve later',
  [BOT_ACTION_CONDITION_TYPE.riverValueBet]:
    'your hand is strong enough at this point to bet and hope weaker hands pay to stay in',
  [BOT_ACTION_CONDITION_TYPE.riverBluff]:
    'this is a believable moment to bet as if your hand is stronger than it is',
  [BOT_ACTION_CONDITION_TYPE.potOddsFail]: {
    preRiver:
      '{facing} does not match how often your hand will win, so keep chips for a better spot',
    river: '{facing} does not match how often your hand will win, so let this one go'
  },
  [BOT_ACTION_CONDITION_TYPE.looseCall]:
    'weaker hands sometimes stay in here, but this moment usually needs a stronger one',
  [BOT_ACTION_CONDITION_TYPE.noGoodHand]:
    'your hand is not strong enough to justify putting more chips in',
  [BOT_ACTION_CONDITION_TYPE.noStrongHand]:
    'your hand is not strong enough to justify putting more chips in',
  [BOT_ACTION_CONDITION_TYPE.isPreFlopAggressor]:
    'you raised before the shared cards came out, so you can keep the other player worried',
  [BOT_ACTION_CONDITION_TYPE.turnGiveUp]:
    'on the fourth shared card, betting again usually does not pay off enough',
  [BOT_ACTION_CONDITION_TYPE.curiosityCall]:
    'this is mostly a guessing game about whether the other player is pretending',
  [BOT_ACTION_CONDITION_TYPE.twoPair]: 'two pair is strong enough to keep adding chips',
  [BOT_ACTION_CONDITION_TYPE.pair]: 'your pair is strong enough to keep playing for {facing}',
  [BOT_ACTION_CONDITION_TYPE.pairOf]: 'your hand is strong enough to play aggressively',
  [BOT_ACTION_CONDITION_TYPE.valueOf]: 'your hand is strong enough to play aggressively',
  [BOT_ACTION_CONDITION_TYPE.twoHigh]:
    'your two high cards still have reasonable value before the shared cards come out',
  [BOT_ACTION_CONDITION_TYPE.flopCheckRaise]:
    'your hand is strong enough to check first, then raise if someone bets',
  [BOT_ACTION_CONDITION_TYPE.betInBoard]:
    'when someone bets into you, you need a stronger hand to keep going',
  [BOT_ACTION_CONDITION_TYPE.noOtherBet]:
    'when everyone passes to you, betting is more likely to work',
  [BOT_ACTION_CONDITION_TYPE.noBetInBoard]:
    'when everyone passes to you, betting is more likely to work',
  [BOT_ACTION_CONDITION_TYPE.moreThan3Players]:
    'with several other players still in, you need a stronger hand to bet',
  [BOT_ACTION_CONDITION_TYPE.unlucky]: {
    preRiver: 'the shared cards missed your hand, so wait for a stronger spot',
    river: 'the shared cards missed your hand, so let this one go'
  },
  [BOT_ACTION_CONDITION_TYPE.bigBlindTurn]:
    'since you already paid the larger forced bet, it costs less to stay in'
}

// ---------------------------------------------------------------------------
// Label formats — the deterministic strings the advisor interpolates into
// labels and copy ({hand}, {street}, action labels). Pure data here so the
// generated output doc enumerates them; the mapping logic stays in advisor.ts.
// ---------------------------------------------------------------------------

// Plain action labels for "Your action" / "Recommended". Verbs are kept
// identical to the action buttons; preflop re-raises get their own label so a
// 3-bet is distinguishable from an open.
export const ACTION_LABELS = {
  fold: 'Fold',
  check: 'Check',
  call: 'Call',
  allin: 'All-in',
  bet: 'Bet',
  raise: 'Raise',
  reRaise: 'Re-raise'
} as const

// Action names as coach phrasing for the {describedAction} / {recommendedAction}
// fillers — the raw `allin` type reads as broken English ("Choose to allin
// instead…"), so it gets a real verb phrase.
export const ACTION_VERB_PHRASES = {
  fold: 'fold',
  check: 'check',
  call: 'call',
  allin: 'go all-in',
  bet: 'bet',
  raise: 'raise'
} as const

// Bet/raise size labels. Preflop opens are rounded to the nearest half big
// ("about" covers the rounding); preflop re-raises are sized RELATIVE to the
// raise faced ("to about 2.5 times their raise") because the absolute total
// in big blinds balloons as the open grows (a standard 3-bet versus a 4.5bb
// open reads as "11 times the big blind" and sounds like a postflop overbet);
// postflop sizing is a share of the pot, with raises describing the amount
// added on top of the call ("by about 50%").
export const SIZING_LABEL_TEMPLATES = {
  preflop: ['to about {multiple} times the big blind', 'to roughly {multiple} times the big blind'],
  preflopReraise: ['to about {multiple} times their raise', 'to roughly {multiple} times their raise'],
  postflopBet: ['about {share}% of the pot', 'around {share}% of the pot'],
  postflopRaise: ['by about {share}% of the pot', 'by roughly {share}% of the pot']
} as const

// Plain, beginner-friendly wording for each betting round — the {street} filler
// (and part of the copy rotation seed). Keep the internal street enums for
// comparisons; use these everywhere the player can read.
export const STREET_PHRASES = {
  preflop: 'at the start of the hand',
  flop: 'with the first three shared cards showing',
  turn: 'with the fourth shared card showing',
  river: 'with all five shared cards showing'
} as const

// Preflop {hand} labels. Pair labels are deliberately singular ("a pair of
// aces", not "pocket aces"): copy templates use singular verbs ("{hand} is a
// favorite"), and a plural label would break subject-verb agreement.
export const PREFLOP_HAND_LABEL_TEMPLATES = {
  pair: ['a pair of {ranks}'],
  suited: ['{hi}-{lo} suited'],
  offsuit: ['{hi}-{lo} offsuit']
} as const

// Made-hand {hand} labels, chosen by the holding analysis in advisor.ts
// (describeMadeHolding). 'boardPair'/'boardTwoPair' are the board-only cases
// ("only the pair on the board") — the hand came from the shared cards, not
// the player's pockets.
export const MADE_HAND_LABELS = {
  twoPair: 'two pair',
  overPair: 'a pocket pair above the board',
  topPair: 'top pair',
  pair: 'a pair',
  boardTwoPair: 'only the two pair on the board',
  boardPair: 'only the pair on the board',
  highCard: 'a hand with no pair'
} as const

// Strong made-hand {hand} labels, matched from the holding description
// (describeStrongMadeHand in advisor.ts).
export const STRONG_MADE_HAND_LABELS = {
  straightFlush: 'a straight flush',
  fourOfAKind: 'four of a kind',
  fullHouse: 'a full house',
  flush: 'a flush',
  straight: 'a straight',
  threeOfAKind: 'three of a kind',
  fallback: 'a strong hand'
} as const

// Draw clauses for the {hand} label ("a flush draw", "a pair with four cards
// of a straight" — see describeDrawClause in advisor.ts).
export const DRAW_CLAUSE_LABELS = {
  flush: 'a flush draw',
  openEndedStraight: 'four cards of a straight',
  gutshotStraight: 'four cards of a straight with a gap'
} as const
